import type { SearchResultKind } from '@aroo/shared';
import { Combobox, Skeleton, TextInput, useCombobox } from '@mantine/core';
import {
  IconArrowRight,
  IconBuildingStore,
  IconSearch,
  IconTruck,
  IconUser,
} from '@tabler/icons-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGlobalSearch } from '../../api/search';
import { UkPlate } from '../../components/plate';
import { useSession } from '../session';
import { useIsMobile } from '../useIsMobile';
import { matchPages, pageShortcuts } from './searchPages';
import classes from './shell.module.css';

interface Hit {
  id: string;
  kind: SearchResultKind | 'page';
  label: string;
  sub: string;
  to: string;
}

interface GlobalSearchProps {
  /** Mobile variant: 44px input with the yellow focus border, full width under the bar. */
  variant: 'desktop' | 'mobile';
  autoFocus?: boolean;
  onNavigate?: () => void;
}

/** Dropdown groups, in this order. */
const GROUPS: { kind: Hit['kind']; label: string }[] = [
  { kind: 'person', label: 'People' },
  { kind: 'site', label: 'Branches' },
  { kind: 'vehicle', label: 'Vehicles' },
  { kind: 'page', label: 'Pages' },
];

function ResultIcon({ kind }: { kind: Hit['kind'] }) {
  const props = { size: 18, stroke: 2, color: 'var(--a-text-muted)' };
  if (kind === 'person') return <IconUser {...props} />;
  if (kind === 'site') return <IconBuildingStore {...props} />;
  if (kind === 'vehicle') return <IconTruck {...props} />;
  return <IconArrowRight {...props} />;
}

/**
 * Global search: people, branches and vehicles from GET /search (only what this person may
 * open, in their scope), plus shortcuts to the screens they can use.
 */
export function GlobalSearch({ variant, autoFocus = false, onNavigate }: GlobalSearchProps) {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [query, setQuery] = useState('');
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  });

  const { access } = useSession();
  const trimmed = query.trim();
  const remote = useGlobalSearch(trimmed);
  const pages = useMemo(() => pageShortcuts(access), [access]);
  const results: Hit[] = useMemo(
    () => [
      ...(trimmed.length >= 2 ? (remote.data?.results ?? []) : []).map((r) => ({
        ...r,
        id: `${r.kind}:${r.id}`,
      })),
      ...matchPages(pages, trimmed).map((p) => ({ ...p, kind: 'page' as const })),
    ],
    [remote.data, pages, trimmed],
  );
  const searching = trimmed.length >= 2 && remote.isFetching && !remote.data;

  const choose = (id: string) => {
    const hit = results.find((r) => r.id === id);
    if (!hit) return;
    setQuery('');
    combobox.closeDropdown();
    onNavigate?.();
    navigate(hit.to);
  };

  const inputClass = variant === 'mobile' ? classes.mobileSearchInput : classes.searchInput;

  return (
    <Combobox
      store={combobox}
      onOptionSubmit={choose}
      width={isMobile ? 'target' : 420}
      position="bottom-start"
      shadow="md"
      withinPortal
    >
      <Combobox.Target>
        <TextInput
          value={query}
          onChange={(e) => {
            setQuery(e.currentTarget.value);
            if (e.currentTarget.value.trim()) combobox.openDropdown();
            else combobox.closeDropdown();
          }}
          onFocus={() => trimmed && combobox.openDropdown()}
          onBlur={() => combobox.closeDropdown()}
          placeholder="Search people, branches, vehicles"
          aria-label="Search"
          leftSection={<IconSearch size={16} stroke={2} />}
          leftSectionProps={{ style: { color: '#A3A3A8' } }}
          classNames={{ input: inputClass }}
          {...(variant === 'desktop' ? { className: classes.search } : {})}
          autoFocus={autoFocus}
          autoComplete="off"
        />
      </Combobox.Target>
      <Combobox.Dropdown hidden={!trimmed}>
        <Combobox.Options mah="min(70vh, 520px)" style={{ overflowY: 'auto' }}>
          {GROUPS.map((g) => {
            const hits = results.filter((r) => r.kind === g.kind);
            if (hits.length === 0) return null;
            return (
              <Combobox.Group key={g.kind} label={g.label}>
                {hits.map((r) => (
                  <Combobox.Option key={r.id} value={r.id} className={classes.result}>
                    <ResultIcon kind={r.kind} />
                    <span className={classes.resultText}>
                      <span className={classes.resultLabel}>
                        {r.kind === 'vehicle' ? (
                          <UkPlate reg={r.label} size="xs" fallback={r.label} />
                        ) : (
                          r.label
                        )}
                      </span>
                      <span className={classes.resultSub}>{r.sub}</span>
                    </span>
                  </Combobox.Option>
                ))}
              </Combobox.Group>
            );
          })}
          {searching && (
            <div className={classes.searchLoading} aria-label="Searching">
              <Skeleton height={12} width="70%" radius="sm" />
              <Skeleton height={12} width="50%" radius="sm" />
            </div>
          )}
          {results.length === 0 && !searching && (
            <Combobox.Empty>
              {trimmed.length < 2
                ? 'Keep typing…'
                : remote.isError
                  ? "Search isn't available right now."
                  : `Nothing matches “${trimmed}”. Try a name, branch or registration.`}
            </Combobox.Empty>
          )}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
}
